import { ResourcesCache } from "@manager/loader/scenarioLoader/ResourcesCache";
import { inject, injectable } from "inversify";
import { Animation } from "../../../model/playerConfiguration/Animation";
import { ScenarioElement } from "../../../model/playerConfiguration/ScenarioElement";
import { ScenarioPath } from "../../../model/scenarioPath/ScenarioPath";
import { TYPES } from "../../di/types";
import { AnimationLayer } from "../../layer/AnimationLayer";
import { ScenarioTimeHelper } from "../util/ScenarioTimeHelper";

export interface DependentAnimationLayerManager {
  newScenarioElement(scenarioPath: ScenarioPath): void;
  resourcesLoaded(resourcesCache: ResourcesCache, totalTime: number): void;
  setTotalTime(totalTime: number): void;
  moveTo(time: number): void;
}

@injectable()
export class DependentAnimationLayerManagerImpl implements DependentAnimationLayerManager {
  private scenarioElement: ScenarioElement;
  private animationsBySrc: Map<string, any>;
  private totalTime: number;

  private currentAnimationSources: string[] = [];

  constructor(@inject(TYPES.layer.AnimationLayer) private layer: AnimationLayer) {}

  newScenarioElement(scenarioPath: ScenarioPath): void {
    this.scenarioElement = scenarioPath.currentElement;
    this.layer.registerObserver(undefined);
  }

  resourcesLoaded(resourcesCache: ResourcesCache, totalTime: number): void {
    this.animationsBySrc = resourcesCache.animationsBySrc;
    this.totalTime = totalTime;
    if (this.findAnimationsAtTime(0).filter((animation) => animation.displayAbovePoster === true).length > 0) {
      this.moveTo(1);
    }
  }

  setTotalTime(totalTime: number): void {
    this.totalTime = totalTime;
  }

  moveTo(time: number): void {
    const timeMillis = time * 1000;
    const animations = this.findAnimationsAtTime(timeMillis);
    const animationsToLoad = animations.filter((animation) => this.currentAnimationSources.indexOf(animation.src) === -1);

    const animationSources = animations.map((animation) => animation.src);
    const animationToRemoveSources = this.currentAnimationSources.filter((src) => animationSources.indexOf(src) === -1);

    for (const animation of animationsToLoad) {
      this.layer.loadAnimation(animation.src, this.animationsBySrc.get(animation.src), animation.animationBlur);
    }
    for (const src of animationToRemoveSources) {
      this.layer.removeAnimation(src);
    }

    const animationToLoadSources = animationsToLoad.map((animation) => animation.src);
    this.currentAnimationSources = this.currentAnimationSources.concat(animationToLoadSources);
    this.currentAnimationSources = this.currentAnimationSources.filter((src) => animationToRemoveSources.indexOf(src) === -1);

    const currentAnimations = animations.filter((animation) => this.currentAnimationSources.indexOf(animation.src) > -1);
    for (const animation of currentAnimations) {
      this.layer.moveTo(animation.src, this.getAnimationRelativeTime(timeMillis, animation));
    }
  }

  private findAnimationsAtTime(timeMillis: number): Animation[] {
    return this.scenarioElement.animations.filter(
      (animation) =>
        ScenarioTimeHelper.getTimeFromVideoStart(this.totalTime, animation.startTimeMillis) <= timeMillis &&
        (animation.endTimeMillis === undefined ||
          ScenarioTimeHelper.getTimeFromVideoStart(this.totalTime, animation.endTimeMillis) >= timeMillis),
    );
  }

  private getAnimationRelativeTime(time: number, animation: Animation) {
    return time - animation.startTimeMillis;
  }
}
